import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  configureEmailProvider,
  configureSmsProvider,
  fetchNotificationProviders,
  removeEmailProvider,
  removeSmsProvider,
  sendEmailProviderTest,
  sendSmsProviderTest,
} from '@/api/notificationProviders/notificationProvidersApi';
import type {
  EmailProviderConfiguration,
  NotificationProviders,
  ProviderTestResult,
  SmsProviderConfiguration,
  SmsProviderTestResult,
} from '@/api/notificationProviders/notificationProvidersApi.types';
import { NOTIFICATION_PROVIDERS_QUERY_KEY } from '@/constants/queryKeys';
import { useAuth } from '@/hooks/useAuth';

export function useNotificationProvidersQuery(): UseQueryResult<NotificationProviders, Error> {
  const { status, activeScopeCode } = useAuth();
  return useQuery({
    queryKey: NOTIFICATION_PROVIDERS_QUERY_KEY,
    queryFn: fetchNotificationProviders,
    enabled: status === 'authenticated' && activeScopeCode === 'tenant',
  });
}

export function useConfigureEmailProviderMutation(): UseMutationResult<
  NotificationProviders,
  Error,
  EmailProviderConfiguration
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: configureEmailProvider,
    onSuccess: function storeUpdated(providers) {
      queryClient.setQueryData(NOTIFICATION_PROVIDERS_QUERY_KEY, providers);
    },
  });
}

export function useConfigureSmsProviderMutation(): UseMutationResult<
  NotificationProviders,
  Error,
  SmsProviderConfiguration
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: configureSmsProvider,
    onSuccess: function storeUpdated(providers) {
      queryClient.setQueryData(NOTIFICATION_PROVIDERS_QUERY_KEY, providers);
    },
  });
}

export function useRemoveEmailProviderMutation(): UseMutationResult<
  NotificationProviders,
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeEmailProvider,
    onSuccess: function storeUpdated(providers) {
      queryClient.setQueryData(NOTIFICATION_PROVIDERS_QUERY_KEY, providers);
    },
  });
}

export function useRemoveSmsProviderMutation(): UseMutationResult<
  NotificationProviders,
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeSmsProvider,
    onSuccess: function storeUpdated(providers) {
      queryClient.setQueryData(NOTIFICATION_PROVIDERS_QUERY_KEY, providers);
    },
  });
}

export function useSendEmailProviderTestMutation(): UseMutationResult<
  ProviderTestResult,
  Error,
  string
> {
  return useMutation({ mutationFn: sendEmailProviderTest });
}

export function useSendSmsProviderTestMutation(): UseMutationResult<
  SmsProviderTestResult,
  Error,
  string
> {
  return useMutation({ mutationFn: sendSmsProviderTest });
}
